import './IndividualStyles.scss';

import { type FC, useMemo } from 'react';
import { sortBy, uniq } from 'lodash-es';

import { FontLicenseType } from 'client/graphql/types/operations';

import IndividualStyle from './IndividualStyle';
import * as cellSizing from './utils/cellSizing';
import groupBy from 'utils/groupBy';
import { usePurchaseTableLayout } from './utils/usePurchaseTableLayout';
import { sortFontFaces } from 'utils/sorting';

interface Props {
  family: {
    id: number;
    completePrice: number;
    subPackageFontFacePrice: number;
    faces: {
      id: number;
      size: { slug: string; name: string; position: number };
      width: { slug: string; name: string; position: number };
      weight: { slug: string; name: string; position: number };
      style: { slug: string; name: string; position: number };
      description: string;
      singleCharacterPreview: { url: string | null };
      family: { fontFacePrice: number; discountedFontFacePrice: number };
    }[];
  };
  licenseOption: {
    id: number;
    type: FontLicenseType;
    multiplier: number;
  } | null;
  hoveredPackageFaceIds: number[];
  disabled?: boolean;
  onHoverFace(identifier: number | null): void;
}

const IndividualStyles: FC<Props> = (props) => {
  const {
    family,
    licenseOption,
    hoveredPackageFaceIds,
    disabled,
    onHoverFace,
  } = props;

  const { packagesStacked } = usePurchaseTableLayout();

  const facesGroupedByAttributes = useMemo(() => {
    const sortedFaces = sortFontFaces(family.faces);

    return groupBy(sortedFaces, (face) => face.width.slug).map(
      (widthGroup) => ({
        name: widthGroup.name,
        weights: groupBy(widthGroup.elements, (face) => face.weight.slug).map(
          (weightGroup) => ({
            name: weightGroup.name,
            styles: groupBy(
              weightGroup.elements,
              (face) => face.style.slug,
            ).map((styleGroup) => ({
              name: styleGroup.name,
              face: styleGroup.elements[0],
            })),
          }),
        ),
      }),
    );
  }, [family]);

  const fontFamilyWidthsNames = useMemo(
    () =>
      uniq(
        sortBy(family.faces, (face) => face.width.position).map(
          (face) => face.width.slug,
        ),
      ),
    [family],
  );

  return (
    <div>
      {facesGroupedByAttributes.map((width) => (
        <div key={width.name} className="IndividualStyles">
          {width.weights.map((weight) => (
            <div key={weight.name} className="IndividualStyles-weightPair">
              {weight.styles.map((style) => (
                <IndividualStyle
                  key={style.name}
                  licenseOption={licenseOption}
                  family={family}
                  face={style.face}
                  disabled={disabled}
                  packageHovered={hoveredPackageFaceIds.includes(style.face.id)}
                  onHover={onHoverFace}
                  doubleHeight={weight.styles.length === 1}
                />
              ))}
            </div>
          ))}

          {fontFamilyWidthsNames.length > 1 && packagesStacked === false && (
            <svg
              className="IndividualStyles-pointer"
              x="0px"
              y="0px"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              width="12px"
              style={{
                height: `calc(${cellSizing.cellSize} * 2)`,
              }}
            >
              <polygon fill="#00000033" points="0,0 0,100 100,50" />
            </svg>
          )}
        </div>
      ))}
    </div>
  );
};

export default IndividualStyles;
